import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, styled, FormHelperText } from '@mui/material';
import React from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import WsColors from '../../WsColors';
import { WsButton } from '../WsButton';
import WsFileUploadIcon, { IconVariant } from '../WsIcon/FileUploadIcon';
import { WsTypography, WsTypoColors, WsTypoVariants } from '../WsTypography';

const StyledBox = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 25,
  borderWidth: 2,
  borderRadius: 2,
  borderColor: WsColors.BorderGrey,
  borderStyle: 'solid',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  position: 'relative',
  textAlign: 'center',
});

const mapColor = new Map();
mapColor.set('jobposting', WsColors.TheBlues800);
mapColor.set('careerpages', WsColors.TheBlues50);
mapColor.set('app', WsColors.White);

const mapVariants = new Map();
mapVariants.set('jobposting', WsTypoColors.WHITE);
mapVariants.set('careerpages', WsTypoColors.BLACK);
mapVariants.set('app', WsTypoColors.SECONDARY);
mapVariants.set('error', WsTypoColors.ERROR);

const WsFileUpload = ({
  multiple = false,
  name,
  onUpload,
  placeholderText = "Drag 'n' drop your resume here, or click to select",
  helperText = 'Drop your document here ...',
  validationMessage = 'File is not in correct format or exceeds Size Limit',
  onFileRejected,
  maxFileSize = 1,
  variant = FileUploadVariants.APP,
  fileTypes = FileTypeVariants.RESUME,
  buttonText = 'upload file',
  Icon,
  placeholderHelperText = '',
  bgRequired = true,
  disabled = false,
}: IFileUploadProps) => {
  const iconVariant =
    variant == FileUploadVariants.CAREERPAGES ? IconVariant.CAREERPAGES : IconVariant.APP;
  const [fileSizeError, setFileSizeError] = React.useState(false);

  const {
    getRootProps: getDocumentRootProps,
    getInputProps: getDocumentInputProps,
    isDragActive: isDocumentDragActive,
    isDragReject,
    isDragAccept,
  } = useDropzone({
    onDropAccepted: onUpload,
    multiple: multiple,
    maxSize: maxFileSize * 1024 * 1024,
    onDropRejected: onFileRejected,
    accept: fileTypes,
    noDrag: variant == FileUploadVariants.JOBPOSTING,
  });

  const enableUploadFileSizeWarning: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = Array.from(event.target.files || []);
    const file = files[0];

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxFileSize) {
      setFileSizeError(true);
    } else {
      setFileSizeError(false);
    }
  };
  return (
    <>
      {bgRequired ? (
        <StyledBox
          {...getDocumentRootProps()}
          sx={{
            bgcolor: mapColor.get(variant),
          }}
        >
          {!Icon && <WsFileUploadIcon variant={iconVariant} />}
          {Icon && Icon}
          <input
            accept={fileTypes}
            {...getDocumentInputProps({
              onChange: (event) => enableUploadFileSizeWarning(event),
            })}
            name={name}
          />
          {isDragReject && !isDragAccept && (
            <Box p={1}>
              <WsTypography color={mapVariants.get('error')} variant={WsTypoVariants.TITLE}>
                {validationMessage}
              </WsTypography>
            </Box>
          )}
          {!isDragReject && (
            <Box p={1}>
              <WsTypography color={mapVariants.get(variant)} variant={WsTypoVariants.TITLE}>
                {isDocumentDragActive ? helperText : placeholderText}
              </WsTypography>
            </Box>
          )}
          {!isDragReject && (
            <Box p={1}>
              <WsTypography color={mapVariants.get(variant)} variant={WsTypoVariants.BODY1}>
                {placeholderHelperText}
              </WsTypography>
            </Box>
          )}
          {fileSizeError && (
            <Box p={1}>
              <WsTypography color={mapVariants.get(variant)} variant={WsTypoVariants.BODY1}>
                {placeholderHelperText}
              </WsTypography>
            </Box>
          )}
          <WsButton
            disabled={disabled}
            startIcon={
              variant == FileUploadVariants.JOBPOSTING ? <></> : <FileUploadOutlinedIcon />
            }
            variant={variant == FileUploadVariants.JOBPOSTING ? 'contained' : 'outlined'}
          >
            {buttonText}
          </WsButton>
        </StyledBox>
      ) : (
        <Box>
          <input
            accept={fileTypes}
            {...getDocumentInputProps({
              onChange: (event) => enableUploadFileSizeWarning(event),
            })}
            name={name}
          />
          <WsButton
            disabled={disabled}
            {...getDocumentRootProps()}
            startIcon={
              variant == FileUploadVariants.JOBPOSTING ? <></> : <FileUploadOutlinedIcon />
            }
            variant={variant == FileUploadVariants.JOBPOSTING ? 'contained' : 'outlined'}
          >
            {buttonText}
          </WsButton>
          {fileSizeError && <FormHelperText error>{validationMessage}</FormHelperText>}
          {isDragReject && !isDragAccept && (
            <FormHelperText error>{validationMessage}</FormHelperText>
          )}
          {!isDragReject && (
            <FormHelperText error>
              {isDocumentDragActive ? helperText : placeholderText}
            </FormHelperText>
          )}
        </Box>
      )}
    </>
  );
};

export interface IFileUploadProps {
  multiple?: boolean;
  name: string;
  onUpload: (files: File[]) => void;
  placeholderText: string;
  placeholderHelperText?: string;
  helperText?: string;
  onFileRejected?: (fileRejections: FileRejection[]) => void;
  maxFileSize?: number;
  buttonText?: string;
  variant?: FileUploadVariants;
  fileTypes?: FileTypeVariants;
  validationMessage?: string;
  Icon?: React.ReactNode;
  bgRequired?: boolean;
  disabled?: boolean;
}

export enum FileUploadVariants {
  CAREERPAGES = 'careerpages',
  APP = 'app',
  JOBPOSTING = 'jobposting',
}

export enum FileTypeVariants {
  RESUME = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.text,text/plain,application/rtf,application/msword, text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/wps-office.docx,application/wps-office.doc',
  BULKUPLOAD = 'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/wps-office.xlsx',
  IMAGES = 'image/png,image/jpeg,image/jpg',
  DOCS_AND_IMAGES = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.text,text/plain,application/rtf,application/msword, text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/wps-office.docx,application/wps-office.doc,image/png,image/jpeg,image/jpg',
}

export default WsFileUpload;
